import React, { JSX, useCallback, useMemo, useRef } from 'react';

import {
    Image,
    ImageSourcePropType,
    LayoutChangeEvent,
    Platform,
    StyleProp,
    Text,
    TextStyle,
    TouchableOpacity,
    ViewStyle,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { SvgProps } from 'react-native-svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import isFunction from 'utils/function';
import { isWeb } from 'utils/responsiveness';

import { ModalPositionType } from 'screens/Detail/components/DetailPageDropdownButton';

import LocalizedText from 'components/LocalizedText';

import styles from './styles';

type Props = {
    accessible?: boolean;
    accessibilityLabel?: string;
    importantForAccessibility?: 'auto' | 'yes' | 'no' | 'no-hide-descendants';
    accessibilityElementsHidden?: boolean;
    testID?: string;
    title: string;
    fillColor?: string;
    buttonStyle?: StyleProp<ViewStyle>;
    buttonTextStyle?: StyleProp<TextStyle>;
    isLocalized?: boolean;
    numberOfLines?: number;
    onPress?: () => void;
    Icon?: React.FC<SvgProps>;
    IconAfter?: JSX.Element;
    icon?: ImageSourcePropType;
    setModalPosition?: React.Dispatch<
        React.SetStateAction<ModalPositionType | undefined>
    >;
    setModalWidth?: React.Dispatch<React.SetStateAction<number>>;
    tabIndex?: number;
    'aria-hidden'?: boolean;
    withHint?: boolean;
};

const {
    ACCESSIBILITY: {
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
    },
} = LANGUAGE_KEYS;

const ActionButton = ({
    accessible,
    accessibilityLabel,
    importantForAccessibility = 'auto',
    accessibilityElementsHidden,
    testID,
    title,
    onPress,
    fillColor,
    buttonStyle,
    buttonTextStyle,
    isLocalized = true,
    numberOfLines,
    icon,
    Icon,
    IconAfter,
    setModalPosition,
    setModalWidth,
    tabIndex = 0,
    'aria-hidden': ariaHidden,
    withHint = false,
}: Props) => {
    const { t } = useTranslation();
    const fillColorStyle = useMemo(
        () =>
            !!fillColor && {
                backgroundColor: fillColor,
            },
        [fillColor],
    );

    const buttonRef = useRef<any | null>(null);

    const updateModalPosition = useCallback(() => {
        if (buttonRef.current && isFunction(setModalPosition)) {
            if (isWeb) {
                const { top, left, width } =
                    buttonRef.current?.getBoundingClientRect();
                setModalPosition({ top, left });

                if (isFunction(setModalWidth)) {
                    setModalWidth(width);
                }
            } else {
                buttonRef.current?.measureInWindow((fx: number, fy: number) => {
                    setModalPosition({
                        top: fy - 126,
                        left: fx,
                    });
                });
            }
        }
    }, [buttonRef, setModalPosition, setModalWidth]);

    const onPressHandler = useCallback(() => {
        if (isFunction(onPress)) {
            onPress();
        }

        updateModalPosition();
    }, [onPress, updateModalPosition]);

    const onLayout = useCallback(
        (e: LayoutChangeEvent) => {
            if (isFunction(setModalWidth)) {
                setModalWidth(e.nativeEvent.layout.width);
            }
        },
        [setModalWidth],
    );

    const accessibilityLabelWithHint = useMemo(
        () =>
            `${accessibilityLabel ?? t(title)} ${
                isWeb && !withHint
                    ? t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB)
                    : ''
            }`,
        [t, withHint, title, accessibilityLabel],
    );

    return (
        <TouchableOpacity
            accessible={accessible}
            accessibilityLabel={accessibilityLabelWithHint}
            importantForAccessibility={importantForAccessibility}
            accessibilityElementsHidden={accessibilityElementsHidden}
            accessibilityRole="button"
            testID={testID}
            style={[styles.buttonStyle, buttonStyle, fillColorStyle]}
            activeOpacity={0.5}
            onPress={onPressHandler}
            ref={buttonRef}
            onLayout={onLayout}
            {...(Platform.OS === 'ios' && {
                accessibilityHint: t(ACCESSIBILITY_GENERAL_TAPP_TWICE),
            })}
            // @ts-ignore
            tabIndex={tabIndex}
            aria-hidden={ariaHidden}
        >
            {!!Icon && <Icon />}
            {icon && <Image source={icon} style={styles.imageStyle} />}
            {isLocalized ? (
                <LocalizedText
                    textKey={title}
                    numberOfLines={numberOfLines}
                    style={[styles.buttonTextStyle, buttonTextStyle]}
                />
            ) : (
                <Text
                    numberOfLines={numberOfLines}
                    style={[styles.buttonTextStyle, buttonTextStyle]}
                >
                    {title}
                </Text>
            )}
            {IconAfter}
        </TouchableOpacity>
    );
};

export default ActionButton;
